// LSP-16345: A Sparky report's display name is derived from its spark/pack title, which in
// each environment carries an internal deployment prefix (e.g. "[PROD] Create a Summary").
// That bracketed environment tag is internal plumbing, not something a customer should ever
// see — most visibly it surfaces on the in-progress report card while cancelling. Strip a
// leading environment tag from the user-facing report name. The allow-list is limited to actual
// deployment-tier tags (the confirmed internal prefix is "[PROD]"); ambiguous tags a customer
// might legitimately choose for a report name — "[QA] Weekly Review", "[Demo] Walkthrough",
// "[Draft] …" — are deliberately NOT stripped, and only the leading position is matched so a
// "[PROD]" appearing mid-name is never altered.
const INTERNAL_ENV_PREFIX = /^\s*(?:\[\s*(?:PROD|PRODUCTION|STAGING|STAGE|DEV|DEVELOP|DEVELOPMENT|LOCAL)\s*\]\s*)+/i;

export function stripInternalReportPrefix(name: string | null | undefined): string {
	if (!name) return name ?? "";
	const stripped = name.replace(INTERNAL_ENV_PREFIX, "").trim();
	// Guard against a name that was ONLY the tag — never return an empty string.
	return stripped || name.trim();
}

/**
 * LSP-19657: the label shown while a report's real name is not known yet.
 *
 * Exported so consumers that need the report's IDENTITY (rather than a table cell's display text)
 * can recognise the placeholder instead of string-matching the literal — see `shareReport` in
 * EntityReports.vue, which must not email this as the report's name.
 */
export const PENDING_REPORT_DISPLAY_NAME = "Report in progress";

/**
 * LSP-19657: the display name for a report row built ONLY from Scribe's status-poll payload.
 *
 * `GET /analyses/status/external/contact/project/:id` returns `{id, status, isRealTime, createdAt,
 * updatedAt, deferredMedia}` — no title, no session reference, no packId. `EntityReports.vue`'s
 * "Scheduled & Running" section builds its rows from that payload, and it used to name them with
 * `getDisplayFileName`, whose last-resort fallback is `timezones.formatDate(createdAt)`. So a
 * project-scoped in-progress report showed a raw timestamp ("09/04/2026 11:12 PM PDT") where its
 * name belongs. (The conversation-scoped tab is unaffected: Scribe's richer results endpoint
 * surfaces those rows promptly, so they always have a real title.)
 *
 * `getDisplayFileName` is NOT changed: for a real file with no title, the upload time is a
 * meaningful identifier, and that helper is shared with the document/file surfaces. The timestamp is
 * only wrong for a report, whose name is not yet known rather than absent.
 *
 * Returns a real title when the payload has one — this is a fallback, not an override — and
 * otherwise a pending label. The row is replaced with the true title as soon as the results endpoint
 * returns it (`mergeStoreAnalyses` only ADDS ids that endpoint has not yet reported).
 */
export function statusOnlyReportDisplayName(analysis: { title?: string | null; name?: string | null } | null | undefined): string {
	const knownName = analysis?.title || analysis?.name;
	if (knownName && knownName.trim()) return stripInternalReportPrefix(knownName);
	return PENDING_REPORT_DISPLAY_NAME;
}

/**
 * LSP-22528: the name of a report that has no title. "Report" is the output's name everywhere — the
 * report viewer already falls back to it — and never a timestamp, which already has its own column.
 */
export const UNTITLED_REPORT_NAME = "Report";

/** LSP-22528: a report row's display name — its title, else its name, else {@link UNTITLED_REPORT_NAME}. */
export function reportDisplayName(analysis: { title?: string | null; name?: string | null }): string {
	const knownName = analysis.title?.trim() || analysis.name?.trim();
	return knownName ? stripInternalReportPrefix(knownName) : UNTITLED_REPORT_NAME;
}
